import { useCallback } from 'react';
import { TranslateFn } from '@blocs.i18n';
import { SELECTION_BTN_SIZES, LETTER_SELECTION_TAGS, NUMBER_SELECTION_TAGS } from './constants';
import { SelectionTagType, SelectionButtonSize, ResponsiveSelectionTagsProps } from './types';
import { WindowWidthContext } from '../../components/utils';

// typeguard function narrows the input type "string" to the union type SelectionButtonSize
const isSelectionSize = (sizeProp: string): sizeProp is SelectionButtonSize =>
  SELECTION_BTN_SIZES.includes(sizeProp as SelectionButtonSize);

export const validateSelectionButtonSize = (sizeProp: string): SelectionButtonSize => {
  if (isSelectionSize(sizeProp)) {
    return sizeProp;
  }

  return 'lg';
};

// typeguard function narrows the input type "string" to the union type SelectionButtonSize
const isValidType = (typeProp: string): typeProp is SelectionTagType =>
  ['number', 'letter'].includes(typeProp as SelectionTagType);
const isLetterType = (typeProp: string): typeProp is 'letter' => typeProp === 'letter';

export const validateSelectionTagType = (typeProp: string): SelectionTagType => {
  if (isValidType(typeProp)) {
    return typeProp;
  }

  return 'number';
};

export const getSelectionTileValuesToUse = (type: string): readonly string[] => {
  const typeToUse = validateSelectionTagType(type);

  if (isLetterType(typeToUse)) {
    return LETTER_SELECTION_TAGS;
  }

  return NUMBER_SELECTION_TAGS;
};

export const useResponsiveSelectionTagProps = () => {
  const { moreSm } = WindowWidthContext.useWidthData();

  const getResponsiveValues = useCallback(
    ({ props = {}, mdProps = {} }: { props: ResponsiveSelectionTagsProps; mdProps: ResponsiveSelectionTagsProps }) => {
      if (!moreSm) {
        return props;
      }

      return { ...props, ...mdProps };
    },
    [moreSm]
  );

  return { getResponsiveValues };
};

export const getToggleButtonAriaLabel = ({
  type,
  selectedValue,
  disabled,
  t,
}: {
  type: 'letter' | 'number';
  selectedValue: string | null;
  disabled: boolean;
  t: TranslateFn;
}) => {
  const combinedSwitchStr = `${selectedValue ? 'set' : 'empty'}${type}${disabled ? 'Disabled' : 'Enabled'}`;
  // combinations that are supported here:
  // type = letter tag, button enabled, no value selected
  // type = letter tag, button disabled, no value selected
  // type = letter tag, button enabled, value selected
  // type = letter tag, button disabled, value selected
  // type = number tag, button enabled, no value selected
  // type = number tag, button disabled, no value selected
  // type = number tag, button enabled, value selected
  // type = number tag, button disabled, value selected

  switch (combinedSwitchStr) {
    case 'emptyletterEnabled':
      return t('common:label.emptyLetterTagEnabled');
    case 'emptyletterDisabled':
      return t('common:label.emptyLetterTagDisabled');
    case 'emptynumberEnabled':
      return t('common:label.emptyNumberTagEnabled');
    case 'emptynumberDisabled':
      return t('common:label.emptyNumberTagDisabled');
    case 'setletterEnabled':
      return t('common:label.setLetterTagEnabled', { tag: selectedValue });
    case 'setletterDisabled':
      return t('common:label.setLetterTagDisabled', { tag: selectedValue });
    case 'setnumberEnabled':
      return t('common:label.setNumberTagEnabled', { tag: selectedValue });
    case 'setnumberDisabled':
      return t('common:label.setNumberTagDisabled', { tag: selectedValue });
    // If none of the cases match, return an empty string or a default value
    default:
      return '';
  }
};
